import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { ChevronLeft, ChevronRight, Calendar as CalIcon, Loader2 } from "lucide-react";

// Compact upcoming-visits calendar. Renders a month grid with dots marking
// days that have events, plus a scrollable list of events beneath.
// - patientId=null → clinic-wide (Dashboard)
// - patientId="TP-…" → single-patient mini view (PatientDetail)
export default function UpcomingCalendar({ patientId = null, days = 60, testid = "upcoming-calendar", onOpenPatient }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(() => { const d = new Date(); return { y: d.getFullYear(), m: d.getMonth() }; });
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = { days };
        if (patientId) params.patient_id = patientId;
        const res = await api.get("/calendar/upcoming", { params });
        if (!cancel) setEvents(res.data?.events || []);
      } catch {
        if (!cancel) setEvents([]);
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [patientId, days]);

  // Group events by ISO date (YYYY-MM-DD).
  const byDate = useMemo(() => {
    const map = new Map();
    for (const e of events) {
      const arr = map.get(e.date) || [];
      arr.push(e);
      map.set(e.date, arr);
    }
    return map;
  }, [events]);

  const monthLabel = useMemo(() => {
    return new Date(month.y, month.m, 1).toLocaleDateString(undefined, { month: "long", year: "numeric" });
  }, [month]);

  // Build the day grid for the selected month.
  const grid = useMemo(() => {
    const first = new Date(month.y, month.m, 1);
    const startWeekday = (first.getDay() + 6) % 7; // Monday=0
    const daysInMonth = new Date(month.y, month.m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      const iso = new Date(month.y, month.m, d).toISOString().slice(0, 10);
      cells.push({ d, iso, events: byDate.get(iso) || [] });
    }
    while (cells.length % 7) cells.push(null);
    return cells;
  }, [month, byDate]);

  const selEvents = selectedDate ? (byDate.get(selectedDate) || []) : events.filter((e) => {
    const d = new Date(e.date);
    return d.getFullYear() === month.y && d.getMonth() === month.m;
  });

  const todayIso = new Date().toISOString().slice(0, 10);
  const shiftMonth = (delta) => {
    setSelectedDate(null);
    setMonth((cur) => {
      const nd = new Date(cur.y, cur.m + delta, 1);
      return { y: nd.getFullYear(), m: nd.getMonth() };
    });
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white overflow-hidden" data-testid={testid}>
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50/60">
        <div className="flex items-center gap-2">
          <CalIcon size={14} className="text-pink-700" />
          <span className="text-sm font-semibold text-slate-800">{monthLabel}</span>
          {loading && <Loader2 size={11} className="animate-spin text-slate-400" />}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => shiftMonth(-1)} className="p-1 rounded hover:bg-slate-100" data-testid={`${testid}-prev-month`}><ChevronLeft size={14} /></button>
          <button
            onClick={() => { const t = new Date(); setMonth({ y: t.getFullYear(), m: t.getMonth() }); setSelectedDate(null); }}
            className="text-[10px] font-medium px-2 py-0.5 rounded border border-slate-300 hover:bg-slate-100"
            data-testid={`${testid}-today`}
          >Today</button>
          <button onClick={() => shiftMonth(+1)} className="p-1 rounded hover:bg-slate-100" data-testid={`${testid}-next-month`}><ChevronRight size={14} /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 gap-px bg-slate-200 border-b border-slate-200 text-[10px] text-slate-500 uppercase tracking-wider">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d} className="bg-slate-50 px-1 py-1 text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-px bg-slate-200">
        {grid.map((cell, i) => {
          if (!cell) return <div key={i} className="bg-slate-50/40 min-h-[42px]" />;
          const isToday = cell.iso === todayIso;
          const isSelected = cell.iso === selectedDate;
          const eventCount = cell.events.length;
          return (
            <button
              key={i}
              onClick={() => setSelectedDate(isSelected ? null : cell.iso)}
              className={`min-h-[42px] p-1 text-left transition text-xs bg-white ${
                isSelected ? "bg-pink-100 ring-2 ring-pink-500" : "hover:bg-slate-50"
              }`}
              data-testid={`${testid}-day-${cell.iso}`}
            >
              <div className={`text-[11px] font-medium ${isToday ? "inline-flex items-center justify-center w-5 h-5 rounded-full bg-pink-600 text-white" : "text-slate-700"}`}>{cell.d}</div>
              {eventCount > 0 && (
                <div className="mt-0.5 flex items-center gap-0.5">
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-pink-600" />
                  <span className="text-[9px] font-semibold text-pink-800">{eventCount}</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
      <div className="max-h-64 overflow-y-auto text-xs">
        {selEvents.length === 0 && (
          <div className="p-3 text-slate-500 italic text-center" data-testid={`${testid}-empty`}>
            {selectedDate ? `No follow-ups on ${selectedDate}` : `No follow-ups this month`}
          </div>
        )}
        {selEvents.map((e, i) => (
          <div
            key={`${e.visit_id}-${i}`}
            className={`border-t border-slate-100 px-3 py-2 flex items-center gap-3 ${onOpenPatient ? "cursor-pointer hover:bg-pink-50/50" : ""}`}
            onClick={() => onOpenPatient && onOpenPatient(e.patient_id, e.visit_id)}
            data-testid={`${testid}-event-${i}`}
          >
            <div className="text-[10px] text-pink-800 font-bold w-14 shrink-0">{e.date}</div>
            <div className="min-w-0 flex-1">
              {!patientId && <div className="font-semibold text-slate-800 truncate">{e.patient_name}</div>}
              <div className="text-[10px] text-slate-500 truncate">
                {e.diagnosis ? `${e.diagnosis} · ` : ""}Follow-up: {e.note}
              </div>
            </div>
            {e.patient_mobile && <div className="text-[10px] text-slate-500 shrink-0 tabular">{e.patient_mobile}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}
